import type { AxiosInstance } from 'axios'
import type { IFactoryFunctionRequest, IFactoryResponse } from '@bc-library/interface'
import { EMethod } from '@bc-library/interface'
import { API_MAIN_VERSION, BASE_URL } from '@/config/constantes'
import useAxios from '@/services/api/useAxios'
import { useUserStore } from '@/config/store/userStore'
import { afterReceiving, beforeSending, beforeSendingPeriod } from '@/services/leave/hooks'
import { afterReceiving as afterReceivingResource } from '@/services/resource/hooks'
import { afterReceiving as afterReceivingLeaves } from '@/services/leave/hooks'
import { afterReceiving as afterReceivingHolidays } from '@/services/publicHoliday/hooks'

let userStore
const getConnectedUser = () => {
  if (!userStore)
    userStore = useUserStore()
  return userStore
}

const resource = 'leaves'

const getInterval = (axios: IFactoryFunctionRequest<any, any>) => ({
  startDate,
  endDate,
  resources = [],
}: { startDate: Date; endDate: Date; resources?: string[] }): IFactoryResponse<Array<any>, any> => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/firmInterval/${getConnectedUser().user._firmId._id}/global`,
    data: { startDate, endDate, resources },
    beforeSendingHook: beforeSendingPeriod,
    afterReceivingHook: afterReceiving,
  })
}

const getProjectLeaves = (axios: IFactoryFunctionRequest<any, any>) => ({ projectId }: { projectId: string }): IFactoryResponse<Array<any>, any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/showProjectLeaves/${projectId}`,
    afterReceivingHook: afterReceiving,
  })
}
const getResource = (axios: IFactoryFunctionRequest) => (id: string): IFactoryResponse<any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/resource/${id}`,
    afterReceivingHook: afterReceiving,
  })
}

const getLeavesBetweenDatesForResources = (axios: IFactoryFunctionRequest<any, any>) => ({
  resourceId,
  startDate,
  endDate,
}: { resourceId: string[]; startDate: Date; endDate: Date }): IFactoryResponse<Array<any>, any> => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/betweenDates/forResources`,
    data: { resourceId, startDate: startDate.toDateString(), endDate: endDate.toDateString() },
    beforeSendingHook: beforeSendingPeriod,
    afterReceivingHook: afterReceiving,
  })
}
const getLeavesRequests = (axios: IFactoryFunctionRequest<any, any>) => (query: any): IFactoryResponse<Array<any>, any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2`,
    query,
    immediate: false,
    afterReceivingHook: afterReceiving,
  })
}

const getLeavesCalendar = (axios: IFactoryFunctionRequest<any, any>) => (query: any): IFactoryResponse<Array<any>, any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/calendar`,
    query,
    afterReceivingHook: (data: any) => {
      // Each resource carries its leaves: convert their periods to calendar days too
      const page = afterReceivingResource(data)
      const withLeaves = (doc: any) => ({ ...doc, leaves: Array.isArray(doc.leaves) ? afterReceiving(doc.leaves) : doc.leaves })
      if (Array.isArray(page)) return page.map(withLeaves)
      if (Array.isArray(page?.docs)) return { ...page, docs: page.docs.map(withLeaves) }
      return page
    }
  })
}

const updateLeave = (axios: IFactoryFunctionRequest<any, any>) => (leave): IFactoryResponse<Array<any>, any> => {
  return axios({
    method: EMethod.put,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2`,
    data: leave,
    beforeSendingHook: beforeSending,
    afterReceivingHook: afterReceiving,    
  })
}

const updateLeavePeriods = (axios: IFactoryFunctionRequest<any, any>) => (leave): IFactoryResponse<Array<any>, any> => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${leave._id}`,
    data: leave,
    beforeSendingHook: beforeSending,
    afterReceivingHook: afterReceiving,    
  })
}

const getLeaveRequest = (axios: IFactoryFunctionRequest<any, any>) => (leaveId): IFactoryResponse<Array<any>, any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${leaveId}`,
    afterReceivingHook: afterReceiving,    
  })
}

const createNewLeaveRequest = (axios: IFactoryFunctionRequest<any, any>) => (payload): IFactoryResponse<Array<any>, any> => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/`,
    data: payload,
    beforeSendingHook: beforeSending,
    afterReceivingHook: afterReceiving,    
  })
}
export type TBulkLeaveResource = {
  _id: string
  firstname: string
  lastname: string
  picture?: string
  currentJob?: {
    position?: { _id: string; display: string }
    office?: { _id: string; display: string; countryId?: string }
  }
}

export type TBulkLeaveFacet = { _id: string; display: string; count: number; countryId?: string }

export type TBulkLeaveResourcesResponse = {
  docs: TBulkLeaveResource[]
  total: number
  hasNextPage: boolean
  facets?: { offices: TBulkLeaveFacet[]; jobs: TBulkLeaveFacet[]; eligibleTotal: number }
}

export type TBulkLeaveCreationStatus = 'created' | 'noAvailableDay' | 'notEligible' | 'error'

export type TBulkLeaveCreationResult = {
  resourceId: string
  status: TBulkLeaveCreationStatus
  days: number
  leaveId: string | null
}

const createNewBulkLeaveRequest = (axios: IFactoryFunctionRequest<TBulkLeaveCreationResult[], any>) => (payload: { type: string; staffingPeriod: any[]; resourceIds: string[] }): IFactoryResponse<TBulkLeaveCreationResult[], any> => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/create/bulk`,
    data: payload,
    beforeSendingHook: beforeSending,
  })
}

const retreiveLeaveBetweenDates = (axios: IFactoryFunctionRequest<{ _id: string; firstname: string; lastname: string; userId: string; user: { email: string } }, any>) => (resourceId: string, startDate: Date, endDate: Date) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${resourceId}/betweenDate/${startDate}/${endDate}`,
    afterReceivingHook: afterReceiving,  
  })
}

/**
 * Resources a leave type can be given to in bulk. `all` returns every result instead of a page,
 * `facets` adds the offices and positions of every eligible resource with their headcount.
 */
const searchResourcesForBulkRequest = (axios: IFactoryFunctionRequest<TBulkLeaveResourcesResponse, any>) => (leaveTypeId: string, query: { search?: string; offices?: string[]; jobs?: string[]; jobCategories?: string[]; page?: number; limit?: number; all?: boolean; facets?: boolean }): IFactoryResponse<TBulkLeaveResourcesResponse, any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/bulk/filterResources/${leaveTypeId}`,
    query,
  })
}

const deleteRequest = (axios: IFactoryFunctionRequest<any, any>) => (leaveId: string): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${leaveId}/deleteRequest`,
    afterReceivingHook: afterReceiving,  
  })
}
const cancellationRequest = (axios: IFactoryFunctionRequest<any, any>) => (leaveId: string): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${leaveId}/cancellationRequest`,
    afterReceivingHook: afterReceiving,  
  })
}

const requestChange = (axios: IFactoryFunctionRequest<any, any>) => (leave): IFactoryResponse<Array<any>, any> => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${leave._id}/requestChange`,
    data: leave,
    beforeSendingHook: beforeSending,
    afterReceivingHook: afterReceiving,    
  })
}

// processUpdateLeave
const acceptChangeRequest = (axios: IFactoryFunctionRequest<any, any>) => (leaveId: string): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${leaveId}/acceptChangeRequest`,
    afterReceivingHook: afterReceiving,  
  })
}

// cancelCancellationRequest
const cancelCancellationRequest = (axios: IFactoryFunctionRequest<any, any>) => (leaveId: string): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${leaveId}/cancelCancellationRequest`,
    afterReceivingHook: afterReceiving,  
  })
}


const rejectCancellationRequest = (axios: IFactoryFunctionRequest<any, any>) => (leaveId: string): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${leaveId}/rejectCancellationRequest`,
    afterReceivingHook: afterReceiving,  
  })
}

const acceptCancellationRequest = (axios: IFactoryFunctionRequest<any, any>) => (leaveId: string): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${leaveId}/acceptCancellationRequest`,
    afterReceivingHook: afterReceiving,  
  })
}

// cancelUpdateLeave
const cancelChangeRequest = (axios: IFactoryFunctionRequest<any, any>) => (leaveId: string): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${leaveId}/cancelChangeRequest`,
    afterReceivingHook: afterReceiving,  
  })
}

const rejectChangeRequest = (axios: IFactoryFunctionRequest<any, any>) => (leaveId: string): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${leaveId}/rejectChangeRequest`,
    afterReceivingHook: afterReceiving,  
  })
}

const exportLeavesCalendar = (axios: IFactoryFunctionRequest<any, any>) => (query) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/calendar/export`,
    query,
    immediate: false,
  })
}

const exportLeavesToExel = (axios: IFactoryFunctionRequest<ILeave, any >) => (query) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/export`,
    query,
    immediate: false,
  })
}

const getLeaveAccountInformations = (axios: IFactoryFunctionRequest<any, any >) => (resourceId) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/account/${resourceId}/informations`,
    afterReceivingHook: afterReceivingResource,
  })
}


const findConflictLeaves = (axios: IFactoryFunctionRequest<any, any >) => () => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/findConflictLeaves`,
    afterReceivingHook: afterReceiving,
  })
}

const countWaitingByResource = (axios: IFactoryFunctionRequest<{ count: number, docs: any[] }, any>) => (resourceId: string): IFactoryResponse<{ count: number, docs: any[] }> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/countWaiting/resource/${resourceId}`,
    afterReceivingHook: (data: { count?: number, docs?: any[] }) => ({
      count: data?.count ?? 0,
      docs: afterReceiving(data?.docs || []),
    }),
  })
}


const deleteLeave = (axios: IFactoryFunctionRequest<any, any>) => (leaveId: string): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.delete,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${leaveId}`,
    afterReceivingHook: afterReceiving,  
  })
}

const deleteConflictLeaves = (axios: IFactoryFunctionRequest<any, any>) => ({departureLeaveIds, holidayLeaveIds}: {departureLeaveIds: string[], holidayLeaveIds: {_id:string, overlappingHolidays: any}[]}): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/deleteConflictLeaves`,
    data: { departureLeaveIds, holidayLeaveIds },
    afterReceivingHook: afterReceiving,  
  })
}

const getAccountsPaginated = (axios: IFactoryFunctionRequest<any, any >) => (query: any): IFactoryResponse<any, any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/accounts/paginated`,
    query,
    afterReceivingHook: (data: any) => {
      return {
        ...data,
        docs: (data.docs || []).map((doc: any) => ({
          ...doc,
          holidays: afterReceivingHolidays(doc.holidays),
          leaves: afterReceivingLeaves(doc.leaves),
          resource: afterReceivingResource(doc.resource)
        }))
      }
    }
  })
}

export default (axios: AxiosInstance) => ({
  getInterval: getInterval(useAxios(axios)),
  getProjectLeaves: getProjectLeaves(useAxios(axios)),
  getLeavesBetweenDatesForResources: getLeavesBetweenDatesForResources(useAxios(axios)),
  getLeavesRequests: getLeavesRequests(useAxios(axios)),
  updateLeave: updateLeave(useAxios(axios)),
  updateLeavePeriods: updateLeavePeriods(useAxios(axios)),
  getLeavesCalendar: getLeavesCalendar(useAxios(axios)),
  getLeaveRequest: getLeaveRequest(useAxios(axios)),
  createNewLeaveRequest: createNewLeaveRequest(useAxios(axios)),
  getResource: getResource(useAxios(axios)),
  createNewBulkLeaveRequest: createNewBulkLeaveRequest(useAxios(axios)),
  retreiveLeaveBetweenDates: retreiveLeaveBetweenDates(useAxios(axios)),
  searchResourcesForBulkRequest: searchResourcesForBulkRequest(useAxios(axios)),
  deleteRequest: deleteRequest(useAxios(axios)),
  cancellationRequest: cancellationRequest(useAxios(axios)),
  cancelCancellationRequest: cancelCancellationRequest(useAxios(axios)),
  acceptCancellationRequest: acceptCancellationRequest(useAxios(axios)),
  rejectCancellationRequest: rejectCancellationRequest(useAxios(axios)),
  requestChange: requestChange(useAxios(axios)),
  acceptChangeRequest: acceptChangeRequest(useAxios(axios)),
  cancelChangeRequest: cancelChangeRequest(useAxios(axios)),
  rejectChangeRequest: rejectChangeRequest(useAxios(axios)),
  exportLeavesToExel: exportLeavesToExel(useAxios(axios)),
  exportLeavesCalendar: exportLeavesCalendar(useAxios(axios)),
  deleteLeave: deleteLeave(useAxios(axios)),
  getLeaveAccountInformations: getLeaveAccountInformations(useAxios(axios)),
  getAccountsPaginated: getAccountsPaginated(useAxios(axios)),
  findConflictLeaves: findConflictLeaves(useAxios(axios)),
  deleteConflictLeaves: deleteConflictLeaves(useAxios(axios)),
  countWaitingByResource: countWaitingByResource(useAxios(axios)),
})
